import type { CSSProperties } from 'react';
import { brandingCssVars, normalizeBranding, readableTextColor } from '@adop/shared';
import type { Branding } from '@adop/shared';
import { mediaUrl } from '../lib/format';

export interface StorePreviewProps {
  name: string;
  about: string | null;
  branding: Branding;
  phone?: string | null;
  addressLines?: string[];
}

/** A miniature of the public storefront header so owners can see branding live. */
export function StorePreview({ name, about, branding, phone, addressLines = [] }: StorePreviewProps) {
  const normalized = normalizeBranding(branding);
  const cssVars = brandingCssVars(branding);
  const onPrimary = readableTextColor(normalized.primary_color);
  const logo = mediaUrl(normalized.logo_key);
  const cover = mediaUrl(normalized.cover_key);

  return (
    <div
      className="overflow-hidden rounded-xl border border-zinc-200 shadow-sm"
      style={cssVars as CSSProperties}
    >
      <div
        className="relative h-28 w-full"
        style={{ backgroundColor: normalized.primary_color }}
      >
        {cover ? <img src={cover} alt="" className="h-full w-full object-cover opacity-80" /> : null}
      </div>
      <div className="relative px-4 pb-4">
        <div
          className="-mt-8 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-4 border-white shadow"
          style={{ backgroundColor: normalized.primary_color, color: onPrimary }}
        >
          {logo ? (
            <img src={logo} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xl font-semibold">{name.trim().charAt(0).toUpperCase() || '?'}</span>
          )}
        </div>
        <div className="mt-3" style={{ fontFamily: 'var(--brand-font)' }}>
          <h3 className="text-lg font-semibold text-zinc-900">{name || 'Your restaurant'}</h3>
          {about ? <p className="mt-1 text-sm text-zinc-600">{about}</p> : null}
          {addressLines.length > 0 || phone ? (
            <p className="mt-2 text-xs text-zinc-500">{[...addressLines, phone].filter(Boolean).join(' · ')}</p>
          ) : null}
        </div>
        <div className="mt-4 flex gap-2">
          <span
            className="rounded-lg px-3 py-2 text-sm font-medium"
            style={{ backgroundColor: normalized.primary_color, color: onPrimary }}
          >
            Order to-go
          </span>
          <span
            className="rounded-lg border px-3 py-2 text-sm font-medium"
            style={{ borderColor: normalized.accent_color, color: normalized.accent_color }}
          >
            Reserve a table
          </span>
        </div>
      </div>
    </div>
  );
}
